import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { BrandProfile } from '@/lib/brand-types';

export type GridMode = 'off_grid' | 'grid_primary';

/** Navigation scope: fleet-level or property-level. Drives sidebar routes and breadcrumb. */
export interface NavigationScope {
  type: 'fleet' | 'property';
  propertyId?: string;
  propertyName?: string;
}

const SCOPE_SESSION_KEY = 'wt-nav-scope';

interface Property {
  id: string;
  name: string;
  org_id: string;
  timezone: string;
  battery_kwh_total: number;
  usable_fraction: number;
  baseline_load_w: number;
  hvac_threshold_add_watts: number;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  battery_reserve_pct: number;
  reserve_source?: string | null;
  /** Backend-authoritative. Use getReadinessState(property) for UI; treat missing as warming_up. */
  readiness_state?: string;
  is_hidden?: boolean;
  /** off_grid | grid_primary. NULL until determined. See GRID_MODES_AND_FORECAST_SEMANTICS. */
  grid_mode?: GridMode | null;
  /** Billing mode: direct (installer pays WT) or reseller (homeowner pays installer). */
  billing_mode?: 'direct' | 'reseller';
  /** Reseller billing lifecycle status. */
  reseller_billing_status?: 'none' | 'pending' | 'active' | 'past_due' | 'canceled' | 'trialing';
  /** What the customer pays per month (cents). */
  customer_price_cents?: number | null;
  /** Flat electricity rate in cents/kWh for savings estimates. */
  electricity_rate_cents_per_kwh?: number | null;
  /** How the electricity rate was set: default, manual, or utility_lookup. */
  rate_source?: 'default' | 'manual' | 'utility_lookup' | 'state_average' | null;
  state_code?: string | null;
  /** Total system cost in cents (e.g. 2500000 = $25,000). */
  system_cost_cents?: number | null;
  /** Date the solar system was installed/commissioned. */
  system_install_date?: string | null;
}

export type AccountTier = 'monitor' | 'alert' | 'predict';

interface Plant {
  id: string;
  property_id: string;
  display_name: string;
  provider_type: string | null;
  provider_plant_id: string | null;
  tier?: AccountTier;
  is_hidden?: boolean;
  last_poll_error?: string | null;
}

interface Organization {
  id: string;
  name: string;
  org_type: 'homeowner' | 'installer' | 'property_manager' | 'services' | 'other';
  /** Billing lifecycle; used for suspended-org gating. */
  billing_status?: 'active' | 'past_due' | 'suspended' | 'trialing' | 'canceled' | 'none';
  default_tier?: AccountTier;
  /** Current billing period end; used for scheduled downgrades. */
  subscription_current_period_end?: string | null;
  /** Stripe Customer id; null when billing not set up (e.g. approval-only signup). */
  stripe_customer_id?: string | null;
  /** White-label brand config. NULL = default Watt Tower branding. */
  brand_profile?: BrandProfile | null;
  /** Stripe Connect: connected account id for reseller billing. */
  stripe_connect_account_id?: string | null;
  stripe_connect_onboarding_complete?: boolean;
  stripe_connect_charges_enabled?: boolean;
  stripe_connect_payouts_enabled?: boolean;
  /** Default price (cents) installer charges customers per property. */
  default_customer_price_cents?: number | null;
  /** Email used for Stripe invoices and billing communications. */
  billing_email?: string | null;
  /** Org-level settings (Phase 1). */
  default_timezone?: string | null;
  default_quiet_hours?: { start: string; end: string; timezone: string } | null;
  feature_flags?: Record<string, boolean> | null;
  notification_contact?: { email?: string; phone?: string; verified_email?: boolean; verified_phone?: boolean } | null;
}

interface UserProfile {
  user_id: string;
  org_id: string | null;
  role: 'homeowner' | 'installer' | 'manager' | 'admin';
  permission_role?: 'owner' | 'admin' | 'viewer';
  full_name: string | null;
  is_super_admin?: boolean;
}

interface AppContextType {
  organization: Organization | null;
  userProfile: UserProfile | null;
  /** Properties visible in lists (excludes hidden unless showHiddenProperties). */
  properties: Property[];
  /** Plants visible in lists (excludes hidden unless showHiddenPlants). */
  plants: Plant[];
  selectedPropertyId: string | null;
  selectedPlantId: string | null;
  selectProperty: (propertyId: string | null) => void;
  selectPlant: (plantId: string | null) => void;
  /** True when role is installer, manager, or admin. Use for installer-only UI (fleet, notifications center, reports, settings tabs). */
  isInstaller: boolean;
  /** True when role is homeowner. Use for vocabulary and gating. */
  isHomeowner: boolean;
  loading: boolean;
  refreshData: () => Promise<void>;
  showHiddenProperties: boolean;
  setShowHiddenProperties: (show: boolean) => void;
  showHiddenPlants: boolean;
  setShowHiddenPlants: (show: boolean) => void;
  /** Super admin: override org to view any org's fleet/data. null = own org. */
  overrideOrgId: string | null;
  setOverrideOrgId: (orgId: string | null) => void;
  /** Navigation scope: fleet vs property. Drives sidebar routes and breadcrumb. */
  navigationScope: NavigationScope;
  setFleetScope: () => void;
  setPropertyScope: (propertyId: string, propertyName: string) => void;
}

const APP_CACHE_KEY = 'wt-app-cache';
const APP_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

function readAppCache(userId: string): { profile: UserProfile; org: Organization | null; properties: Property[]; plants: Plant[] } | null {
  try {
    const raw = sessionStorage.getItem(`${APP_CACHE_KEY}-${userId}`);
    if (!raw) return null;
    const { at, profile, org, properties, plants } = JSON.parse(raw);
    if (Date.now() - at > APP_CACHE_TTL_MS) return null;
    if (!profile || !Array.isArray(properties) || !Array.isArray(plants)) return null;
    return { profile, org: org ?? null, properties, plants };
  } catch {
    return null;
  }
}

function writeAppCache(userId: string, profile: UserProfile, org: Organization | null, properties: Property[], plants: Plant[]) {
  try {
    sessionStorage.setItem(
      `${APP_CACHE_KEY}-${userId}`,
      JSON.stringify({ at: Date.now(), profile, org, properties, plants })
    );
  } catch {
    // ignore
  }
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [allPlants, setAllPlants] = useState<Plant[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showHiddenProperties, setShowHiddenProperties] = useState(false);
  const [showHiddenPlants, setShowHiddenPlants] = useState(false);
  const [overrideOrgId, setOverrideOrgIdRaw] = useState<string | null>(null);

  // Navigation scope — default fleet, persisted to sessionStorage
  const [navigationScope, setNavigationScope] = useState<NavigationScope>(() => {
    try {
      const raw = sessionStorage.getItem(SCOPE_SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as NavigationScope;
        if (parsed.type === 'property' && parsed.propertyId) return parsed;
      }
    } catch { /* ignore */ }
    return { type: 'fleet' };
  });

  const setFleetScope = () => {
    const scope: NavigationScope = { type: 'fleet' };
    setNavigationScope(scope);
    setSelectedPropertyId(null);
    setSelectedPlantId(null);
    try { sessionStorage.setItem(SCOPE_SESSION_KEY, JSON.stringify(scope)); } catch { /* ignore */ }
  };

  const setPropertyScope = (propertyId: string, propertyName: string) => {
    const scope: NavigationScope = { type: 'property', propertyId, propertyName };
    setNavigationScope(scope);
    // Sync selectedPropertyId so all pages see the scoped property
    setSelectedPropertyId(propertyId);
    if (plants.length > 0) {
      const first = plants.find((p) => p.property_id === propertyId);
      setSelectedPlantId(first?.id ?? null);
    }
    try { sessionStorage.setItem(SCOPE_SESSION_KEY, JSON.stringify(scope)); } catch { /* ignore */ }
  };

  const properties = showHiddenProperties
    ? allProperties
    : allProperties.filter((p) => !p.is_hidden);
  const plants = showHiddenPlants
    ? allPlants
    : allPlants.filter((p) => !p.is_hidden);

  const fetchData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // [perf] DIAG: AppContext (profile + properties + plants) timing (remove after diagnosis)
    let t0 = 0;
    try {
      t0 = performance.now();
      // Fetch user profile
      const { data: profile } = await supabase
        .from('users_profile')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setUserProfile(profile as UserProfile);

        // Fetch organization
        if (profile.org_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', profile.org_id)
            .single();
          
          if (org) {
            setOrganization(org as Organization);
          }

          // Fetch properties
          const { data: props } = await supabase
            .from('properties')
            .select('*')
            .eq('org_id', profile.org_id)
            .order('name');

          if (props && props.length > 0) {
            setAllProperties(props as Property[]);

            // Fetch plants for these properties
            const propertyIds = props.map(p => p.id);
            const { data: plantsData } = await supabase
              .from('plants')
              .select('*')
              .in('property_id', propertyIds)
              .order('display_name');

            if (plantsData) {
              setAllPlants(plantsData as Plant[]);
              writeAppCache(user.id, profile as UserProfile, org as Organization | null, props as Property[], plantsData as Plant[]);
              // Auto-select property: use persisted scope if valid, else first property
              if (!selectedPropertyId) {
                const scopedId = navigationScope.type === 'property' && navigationScope.propertyId
                  && props.find(p => p.id === navigationScope.propertyId)
                  ? navigationScope.propertyId
                  : props[0].id;
                setSelectedPropertyId(scopedId);
                const firstPlant = plantsData.find(p => p.property_id === scopedId);
                if (firstPlant) setSelectedPlantId(firstPlant.id);
              }
            }
          } else {
            setAllProperties([]);
            setAllPlants([]);
          }
        } else {
          // No org (e.g. org deleted, user reset for re-onboarding): clear state and cache so onboarding shows
          setOrganization(null);
          setAllProperties([]);
          setAllPlants([]);
          setSelectedPropertyId(null);
          setSelectedPlantId(null);
          try {
            sessionStorage.removeItem(`${APP_CACHE_KEY}-${user.id}`);
          } catch {
            // ignore
          }
        }
      }
    } catch (error) {
      console.error('Error fetching app data:', error);
    } finally {
      const duration = t0 ? Math.round(performance.now() - t0) : 0;
      if (typeof console !== 'undefined' && console.log) {
        console.log(`[perf] app-context (profile+properties+plants): ${duration}ms`);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const cached = readAppCache(user.id);
    // Only use cache when user has an org (avoid stale cache after org/user reset for re-onboarding)
    if (cached && cached.profile.org_id) {
      setUserProfile(cached.profile);
      setOrganization(cached.org);
      setAllProperties(cached.properties);
      setAllPlants(cached.plants);
      if (!selectedPropertyId && cached.properties.length > 0) {
        // Restore from persisted navigation scope if available
        const scopedId = navigationScope.type === 'property' && navigationScope.propertyId
          && cached.properties.find(p => p.id === navigationScope.propertyId)
          ? navigationScope.propertyId
          : cached.properties[0].id;
        setSelectedPropertyId(scopedId);
        const firstPlant = cached.plants.find(p => p.property_id === scopedId);
        if (firstPlant) setSelectedPlantId(firstPlant.id);
      }
      setLoading(false);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when user changes; omit fetchData to avoid loop (stable by design)
  }, [user]);

  // Auto-select first plant when property changes
  useEffect(() => {
    if (selectedPropertyId && plants.length > 0) {
      const propertyPlants = plants.filter(p => p.property_id === selectedPropertyId);
      if (propertyPlants.length > 0 && !selectedPlantId) {
        setSelectedPlantId(propertyPlants[0].id);
      } else if (!propertyPlants.find(p => p.id === selectedPlantId)) {
        setSelectedPlantId(propertyPlants[0]?.id ?? null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync plant when property/plants change; omit selectedPlantId to avoid loop
  }, [selectedPropertyId, plants]);

  const selectProperty = (propertyId: string | null) => {
    setSelectedPropertyId(propertyId);
    if (propertyId && plants.length > 0) {
      const first = plants.find((p) => p.property_id === propertyId);
      setSelectedPlantId(first?.id ?? null);
    } else {
      setSelectedPlantId(null);
    }
  };

  const selectPlant = (plantId: string | null) => {
    setSelectedPlantId(plantId);
  };

  // Super admin org override: load another org's properties/plants when overrideOrgId is set.
  // user_can_access_org allows super admin to query any org via RLS.
  const setOverrideOrgId = (orgId: string | null) => {
    setOverrideOrgIdRaw(orgId);
    if (!orgId) {
      // Revert to own org
      fetchData();
    }
  };

  useEffect(() => {
    if (!overrideOrgId || !userProfile?.is_super_admin) return;

    let cancelled = false;
    const loadOverrideOrg = async () => {
      setLoading(true);
      try {
        const { data: org } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', overrideOrgId)
          .single();

        if (cancelled) return;
        if (org) setOrganization(org as Organization);

        const { data: props } = await supabase
          .from('properties')
          .select('*')
          .eq('org_id', overrideOrgId)
          .order('name');

        if (cancelled) return;
        if (props && props.length > 0) {
          setAllProperties(props as Property[]);
          const propertyIds = props.map((p) => p.id);
          const { data: plantsData } = await supabase
            .from('plants')
            .select('*')
            .in('property_id', propertyIds)
            .order('display_name');

          if (cancelled) return;
          setAllPlants((plantsData ?? []) as Plant[]);
          setSelectedPropertyId(props[0].id);
          const firstPlant = plantsData?.find((p) => p.property_id === props[0].id);
          setSelectedPlantId(firstPlant?.id ?? null);
        } else {
          setAllProperties([]);
          setAllPlants([]);
          setSelectedPropertyId(null);
          setSelectedPlantId(null);
        }
      } catch (err) {
        console.error('[AppContext] Override org load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOverrideOrg();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideOrgId]);

  const isInstaller = ['installer', 'manager', 'admin'].includes(userProfile?.role ?? '');
  const isHomeowner = userProfile?.role === 'homeowner';

  return (
    <AppContext.Provider
      value={{
        organization,
        userProfile,
        properties,
        plants,
        selectedPropertyId,
        selectedPlantId,
        selectProperty,
        selectPlant,
        isInstaller,
        isHomeowner,
        loading,
        refreshData: fetchData,
        showHiddenProperties,
        setShowHiddenProperties,
        showHiddenPlants,
        setShowHiddenPlants,
        overrideOrgId,
        setOverrideOrgId,
        navigationScope,
        setFleetScope,
        setPropertyScope,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
